import {
  DownloadSimpleIcon,
  EyeClosedIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TagIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useBulkChangeCategoryMarketShopInventory } from '@/api/market-shop/inventory/bulk-change-category-market-shop-inventory'
import { useBulkDeleteMarketShopInventory } from '@/api/market-shop/inventory/bulk-delete-market-shop-inventory'
import { useBulkHideMarketShopInventory } from '@/api/market-shop/inventory/bulk-hide-market-shop-inventory'
import { useBulkUnhideMarketShopInventory } from '@/api/market-shop/inventory/bulk-unhide-market-shop-inventory'
import { useChangeCategoryMarketShopInventoryItem } from '@/api/market-shop/inventory/change-category-market-shop-inventory-item'
import { useDeleteMarketShopInventoryItem } from '@/api/market-shop/inventory/delete-market-shop-inventory-item'
import { useExportMarketShopInventory } from '@/api/market-shop/inventory/export-market-shop-inventory'
import {
  getMarketShopInventory,
  getMarketShopInventoryQueryKey,
  useGetMarketShopInventory,
} from '@/api/market-shop/inventory/get-market-shop-inventory'
import { useGetMarketShopInventoryTags } from '@/api/market-shop/inventory/get-market-shop-inventory-tags'
import { useHideMarketShopInventoryItem } from '@/api/market-shop/inventory/hide-market-shop-inventory-item'
import { useUnhideMarketShopInventoryItem } from '@/api/market-shop/inventory/unhide-market-shop-inventory-item'
import { useGetMarketShopLink } from '@/api/market-shop/settings/get-market-shop-link'
import { useGetMarketShopSettings } from '@/api/market-shop/settings/get-market-shop-settings'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Icon } from '@/components/ui/icon'
import { Select, SelectOption } from '@/components/ui/select'
import { StatusTabOption, StatusTabs } from '@/components/ui/status-tabs'
import { BulkAction, RowAction, SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { popupFactory, routerFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import {
  MarketShopInventoryItem,
  MarketShopInventoryItemStatus,
  MarketShopInventoryItemStatusEnum,
} from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import { ChangeCategoryModal } from './change-category-modal'
import {
  getMarketShopInventoryColumns,
  marketShopInventoryInitialFilters,
  marketShopInventoryQueryFiltersSchema,
} from './market-shop-inventory.constants'
import { useDefaultFiltering } from './market-shop-inventory.utils'

export const MarketShopInventoryPage = () => {
  const { t } = useTranslation(['market-shop-dashboard', 'common'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id
  const { navigate } = routerFactory.useRouter()
  const { data: marketShopLink } = useGetMarketShopLink({ buildingId })
  const marketShopId = marketShopLink?.[0]?.id
  const { data: marketShopSettings } = useGetMarketShopSettings({ marketShopId })
  const { data: tags, isFetching: isTagsFetching } = useGetMarketShopInventoryTags({ buildingId })
  const queryClient = useQueryClient()

  const searchParams = useSearchParams({
    initialState: marketShopInventoryInitialFilters,
    schema: marketShopInventoryQueryFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: marketShopInventoryInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const rawSearch = filters.getFilter('search')
  const search = rawSearch?.trim()
  const categoryId = filters.getFilter('categoryId') ?? []
  const tagId = filters.getFilter('tagId') ?? []
  const status = filters.getFilter('status')

  const {
    data,
    isFetching: isInventoryFetching,
    error: inventoryError,
  } = useGetMarketShopInventory({
    buildingId: buildingId ?? 0,
    categoryId:
      categoryId && categoryId.length > 0
        ? categoryId.includes('all')
          ? undefined
          : categoryId
        : undefined,
    limit: filters.getFilter('limit'),
    order: filters.getFilter('sortOrder'),
    search: search || undefined,
    skip: filters.getFilter('skip'),
    sort: filters.getFilter('sortBy'),
    status,
    tagId: tagId && tagId.length > 0 ? tagId : undefined,
  })

  const { total = 0, data: inventoryItems = [], counts } = data ?? {}

  const resolveAllMatchingInventory = async (): Promise<MarketShopInventoryItem[]> => {
    if (!buildingId || !total) return []

    const request = {
      buildingId,
      categoryId:
        categoryId && categoryId.length > 0
          ? categoryId.includes('all')
            ? undefined
            : categoryId
          : undefined,
      limit: total,
      order: filters.getFilter('sortOrder'),
      search: search || undefined,
      skip: 0,
      sort: filters.getFilter('sortBy'),
      status,
      tagId: tagId && tagId.length > 0 ? tagId : undefined,
    }

    const response = await queryClient.fetchQuery({
      queryFn: () => getMarketShopInventory(request),
      queryKey: getMarketShopInventoryQueryKey(request),
    })

    return response.data
  }

  const { mutateAsync: downloadInventory, isPending: isDownloading } =
    useExportMarketShopInventory()

  const { mutateAsync: bulkHide } = useBulkHideMarketShopInventory()
  const { mutateAsync: bulkUnhide } = useBulkUnhideMarketShopInventory()
  const { mutateAsync: bulkDelete } = useBulkDeleteMarketShopInventory()
  const { mutateAsync: bulkChangeCategory } = useBulkChangeCategoryMarketShopInventory()
  const { mutateAsync: hideItem } = useHideMarketShopInventoryItem()
  const { mutateAsync: unhideItem } = useUnhideMarketShopInventoryItem()
  const { mutateAsync: deleteItem } = useDeleteMarketShopInventoryItem()
  const { mutateAsync: changeCategoryItem } = useChangeCategoryMarketShopInventoryItem()

  useDefaultFiltering({ categories: marketShopSettings?.categories, filters, searchParams })

  const marketShopInventoryColumns = useMemo(() => getMarketShopInventoryColumns(t), [t])

  const statusTabOptions: StatusTabOption<MarketShopInventoryItemStatus>[] = [
    {
      count: counts?.['active'],
      label: t('market-shop-dashboard:inventory.statusLabels.active'),
      value: MarketShopInventoryItemStatusEnum.enum['active'],
    },
    {
      color: 'red',
      count: counts?.['outOfStock'],
      label: t('market-shop-dashboard:inventory.statusLabels.outOfStock'),
      value: MarketShopInventoryItemStatusEnum.enum['outOfStock'],
    },
    {
      count: counts?.['hidden'],
      label: t('market-shop-dashboard:inventory.statusLabels.hidden'),
      value: MarketShopInventoryItemStatusEnum.enum['hidden'],
    },
  ]

  const categoryOptions: SelectOption<number | string>[] = [
    {
      label: t('market-shop-dashboard:inventory.allCategoryLabel'),
      value: 'all',
    },
    ...(marketShopSettings?.categories ?? []).map((category) => ({
      label: category.name,
      value: category.id,
    })),
  ]

  const tagOptions =
    tags?.map((tag) => ({
      label: tag.name === 'NEW' ? t('market-shop-dashboard:inventory.tagNewLabel') : tag.name,
      value: tag.id,
    })) ?? []

  const handleStatusChange = (newStatus: MarketShopInventoryItemStatus) => {
    filters.setFilter('status', newStatus)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    filters.setFilter('search', e.target.value)
  }

  const handleCategoryChange = (selected: (number | string)[]) => {
    filters.setFilter('categoryId', selected)
  }

  const handleTagChange = (selected: number[]) => {
    filters.setFilter('tagId', selected)
  }

  const handleSort = (sortBy: string | null, sortOrder: SortOrder | null) => {
    filters.setFilters({
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
    })
  }

  const handleDownloadCSV = () => {
    if (!activeBuilding) return

    const trimmed = activeBuilding.name?.trim()
    const date = new Date().toISOString().split('T')[0]
    const fileName = `inventory_export_${date}.csv`
    const namedFileName = trimmed ? `${trimmed}-${fileName}` : fileName

    void downloadInventory({ buildingId: activeBuilding.id, fileName: namedFileName })
  }

  const bulkActions: BulkAction<MarketShopInventoryItem>[] = [
    {
      icon: PencilIcon,
      label: t('market-shop-dashboard:inventory.bulkActions.changeCategory'),
      onClick: (items) => {
        if (!buildingId) return

        popupFactory.open(ChangeCategoryModal, {
          categoryOptions,
          items,
          onConfirm: async (selectedCategoryId) => {
            await bulkChangeCategory({
              buildingId: buildingId ?? 0,
              ...(selectedCategoryId !== 'all' ? { categoryId: Number(selectedCategoryId) } : {}),
              ids: items.map((item) => item.id),
            })
          },
        })
      },
    },
    {
      hidden: () => status !== MarketShopInventoryItemStatusEnum.enum['active'],
      icon: EyeClosedIcon,
      label: t('market-shop-dashboard:inventory.bulkActions.hide.label'),
      onClick: (items) => {
        if (!buildingId) return

        popupFactory.open(ConfirmationPopup, {
          children: t('market-shop-dashboard:inventory.bulkActions.hide.confirmBody', {
            count: items.length,
          }),
          confirmIcon: EyeClosedIcon,
          confirmText: t('market-shop-dashboard:inventory.bulkActions.hide.label'),
          onConfirm: async () => {
            await bulkHide({ buildingId: buildingId ?? 0, ids: items.map((item) => item.id) })
          },
          title: t('market-shop-dashboard:inventory.bulkActions.hide.confirmTitle'),
          variant: 'warning',
        })
      },
    },
    {
      hidden: () => status !== MarketShopInventoryItemStatusEnum.enum['hidden'],
      icon: EyeIcon,
      label: t('market-shop-dashboard:inventory.bulkActions.unhide.label'),
      onClick: (items) => {
        if (!buildingId) return

        popupFactory.open(ConfirmationPopup, {
          children: t('market-shop-dashboard:inventory.bulkActions.unhide.confirmBody', {
            count: items.length,
          }),
          confirmIcon: EyeIcon,
          confirmText: t('market-shop-dashboard:inventory.bulkActions.unhide.label'),
          onConfirm: async () => {
            await bulkUnhide({ buildingId: buildingId ?? 0, ids: items.map((item) => item.id) })
          },
          title: t('market-shop-dashboard:inventory.bulkActions.unhide.confirmTitle'),
          variant: 'warning',
        })
      },
    },
    {
      icon: TrashIcon,
      iconColor: 'red500',
      onClick: (items) => {
        if (!buildingId) return

        popupFactory.open(ConfirmationPopup, {
          children: t('market-shop-dashboard:inventory.bulkActions.delete.confirmBody', {
            count: items.length,
          }),
          confirmIcon: TrashIcon,
          confirmText: t('common:actions.delete'),
          onConfirm: async () => {
            await bulkDelete({
              buildingId: buildingId ?? 0,
              ids: items.map((item) => item.id),
            })
          },
          title: t('market-shop-dashboard:inventory.bulkActions.delete.confirmTitle'),
          variant: 'danger',
        })
      },
    },
  ]

  const rowActions: RowAction<MarketShopInventoryItem>[] = [
    {
      icon: TagIcon,
      label: t('market-shop-dashboard:inventory.bulkActions.changeCategory'),
      onClick: (item) => {
        if (!buildingId) return

        popupFactory.open(ChangeCategoryModal, {
          categoryOptions: (marketShopSettings?.categories ?? []).map((category) => ({
            label: category.name,
            value: category.id,
          })),
          items: [item],
          onConfirm: async (selectedCategoryId) => {
            await changeCategoryItem({
              buildingId: buildingId ?? 0,
              ...(selectedCategoryId !== 'all' ? { categoryId: Number(selectedCategoryId) } : {}),
              id: item.id,
            })
          },
        })
      },
    },
    {
      // Note: Don't render the primary action
      hidden: () => true,
      icon: EyeIcon,
      label: t('market-shop-dashboard:inventory.rowActions.viewEdit'),
      onClick: (item) => {
        navigate({
          params: { id: item.id },
          path: '/dashboard/building-dashboard/market-shop/inventory/:id',
        })
      },
      primary: true,
    },
    {
      hidden: (item) => item.hidden,
      icon: EyeClosedIcon,
      label: t('market-shop-dashboard:inventory.bulkActions.hide.label'),
      onClick: (item) => {
        if (!buildingId) return

        popupFactory.open(ConfirmationPopup, {
          children: t('market-shop-dashboard:inventory.rowActions.hide.confirmBody', {
            summary: item.summary,
          }),
          confirmIcon: EyeClosedIcon,
          confirmText: t('market-shop-dashboard:inventory.bulkActions.hide.label'),
          onConfirm: async () => {
            await hideItem({ buildingId: buildingId ?? 0, id: item.id })
          },
          title: t('market-shop-dashboard:inventory.bulkActions.hide.confirmTitle'),
          variant: 'warning',
        })
      },
    },
    {
      hidden: (item) => !item.hidden,
      icon: EyeIcon,
      label: t('market-shop-dashboard:inventory.bulkActions.unhide.label'),
      onClick: (item) => {
        if (!buildingId) return

        popupFactory.open(ConfirmationPopup, {
          children: t('market-shop-dashboard:inventory.rowActions.unhide.confirmBody', {
            summary: item.summary,
          }),
          confirmIcon: EyeIcon,
          confirmText: t('market-shop-dashboard:inventory.bulkActions.unhide.label'),
          onConfirm: async () => {
            await unhideItem({ buildingId: buildingId ?? 0, id: item.id })
          },
          title: t('market-shop-dashboard:inventory.bulkActions.unhide.confirmTitle'),
          variant: 'warning',
        })
      },
    },
    {
      icon: TrashIcon,
      label: t('common:actions.delete'),
      onClick: (item) => {
        if (!buildingId) return

        popupFactory.open(ConfirmationPopup, {
          children: t('market-shop-dashboard:dashboard.editProduct.deleteConfirmation.body', {
            summary: item.summary,
          }),
          confirmIcon: TrashIcon,
          confirmText: t('common:actions.delete'),
          onConfirm: async () => {
            await deleteItem({ buildingId: buildingId ?? 0, id: item.id })
          },
          title: t('market-shop-dashboard:dashboard.editProduct.deleteConfirmation.title'),
          variant: 'danger',
        })
      },
      variant: 'danger',
    },
  ]

  return (
    <TableGroup
      id="market-shop-inventory"
      columns={marketShopInventoryColumns}
      data={inventoryItems}
      error={inventoryError}
      getRowKey={(item) => item.id}
      loading={isInventoryFetching}
      tableFilters={filters}
      mode="bulk"
      bulkActions={bulkActions}
      selectAllMatching={{ resolveAll: resolveAllMatchingInventory, total }}
      rowActions={rowActions}
      filters={
        <>
          <Select
            options={tagOptions}
            type="multi"
            placeholder={t('market-shop-dashboard:inventory.tagsFilterPlaceholder')}
            loading={isTagsFetching}
            size="small"
            value={tagId ?? []}
            onReset={() => handleTagChange([])}
            onChange={handleTagChange}
          />
          <Select
            options={categoryOptions}
            type="multi"
            placeholder={t('market-shop-dashboard:inventory.categoriesFilterPlaceholder')}
            size="small"
            value={categoryId ?? []}
            onReset={() => handleCategoryChange([])}
            onChange={handleCategoryChange}
          />
        </>
      }
      navigation={{
        actions: (
          <Button
            onlyBox
            use="secondary"
            size="small"
            title={t('market-shop-dashboard:inventory.downloadCsv')}
            loading={isDownloading}
            disabled={!activeBuilding}
            onClick={handleDownloadCSV}
          >
            <Icon icon={DownloadSimpleIcon} />
          </Button>
        ),
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            placeholder={t('market-shop-dashboard:inventory.searchPlaceholder')}
            size="small"
            value={rawSearch}
            onChange={handleSearchChange}
          />
        ),
        statusTabs: (
          <StatusTabs
            options={statusTabOptions}
            value={status ?? null}
            onChange={handleStatusChange}
          />
        ),
      }}
      pagination={{
        paginationFilters: filters,
        total,
      }}
      sortBy={filters.getFilter('sortBy')}
      sortOrder={filters.getFilter('sortOrder')}
      onSort={handleSort}
    />
  )
}
